/**
 * CrudToolbar — 표준 CRUD 툴바 (조회/추가/수정/삭제/저장/취소/엑셀/출력).
 *
 * 【역할】
 * - 페이지 상단에 일관된 CRUD 버튼 행을 렌더.
 * - permissions 맵(R/C/U/D/E/P) 과 각 on* 핸들러 유무로 버튼 표시 여부 결정.
 * - dirty 플래그가 true 일 때만 저장 버튼 활성화.
 *
 * 【주요 기능】
 * - Flex + Space 로 우측 정렬 배치.
 * - 라벨은 useLabel() t() 로 i18n 처리 (BTN_SEARCH/ADD/EDIT/DELETE/SAVE/CANCEL/EXCEL/PRINT).
 * - permissions 미지정 시 R/C/U/D/E/P 모두 true 로 간주.
 *
 * 【Props】
 * - onSearch/onAdd/onEdit/onDelete/onSave/onCancel/onExport/onPrint?: () => void — 각 버튼 핸들러.
 * - menuKey?: string — 권한 체크용 메뉴 키.
 * - permissions?: Partial<Record<PermAction, boolean>> — 액션 허용 맵 (R/C/U/D/E/P).
 * - loading?: boolean — 조회 버튼 로딩 상태.
 * - dirty?: boolean — 저장 버튼 활성화 플래그.
 * - style?: CSSProperties — Flex 컨테이너 스타일 오버라이드.
 *
 * 【사용 예시】
 * ```tsx
 * <CrudToolbar onAdd={add} onDelete={del} onSave={save} dirty={dataset.isDirty} />
 * ```
 */
import type { CSSProperties } from 'react'
import { Space, Button, Flex } from 'antd'
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  CloseOutlined,
  DownloadOutlined,
  PrinterOutlined
} from '@ant-design/icons'
import type { PermAction } from '@/framework/usePermission'
import { useLabel } from '@/framework/useLabel'

export interface CrudToolbarProps {
  /** 조회 버튼 핸들러. 미지정 시 버튼 미렌더. */
  onSearch?: () => void
  /** 추가 버튼 핸들러. 미지정 시 버튼 미렌더. */
  onAdd?: () => void
  /** 수정 버튼 핸들러. 미지정 시 버튼 미렌더. */
  onEdit?: () => void
  /** 삭제 버튼 핸들러. 미지정 시 버튼 미렌더. */
  onDelete?: () => void
  /** 저장 버튼 핸들러. 미지정 시 버튼 미렌더. dirty=true 일 때만 활성화. */
  onSave?: () => void
  /** 취소 버튼 핸들러. 미지정 시 버튼 미렌더. */
  onCancel?: () => void
  /** 엑셀 다운로드 핸들러. 미지정 시 버튼 미렌더. */
  onExport?: () => void
  /** 출력 핸들러. 미지정 시 버튼 미렌더. */
  onPrint?: () => void
  /** 메뉴 키 (권한 체크용). */
  menuKey?: string
  /** 활성화된 액션만 표시. 미지정 시 R/C/U/D/E/P 모두 true. */
  permissions?: Partial<Record<PermAction, boolean>>
  /** 조회 버튼 로딩 상태. */
  loading?: boolean
  /** true 일 때만 저장 버튼 활성화. */
  dirty?: boolean
  /** 컨테이너 스타일 오버라이드. */
  style?: CSSProperties
}

/**
 * CrudToolbar — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function CrudToolbar(props: CrudToolbarProps) {
  const { t } = useLabel()
  const p = props.permissions ?? { R: true, C: true, U: true, D: true, E: true, P: true }
  return (
    <Flex justify="flex-end" style={{ width: '100%', ...props.style }}>
      <Space size="small" wrap>
        {p.R && props.onSearch && (
          <Button icon={<SearchOutlined />} onClick={props.onSearch} loading={props.loading}>
            {t('BTN_SEARCH', '조회')}
          </Button>
        )}
        {p.C && props.onAdd && (
          <Button icon={<PlusOutlined />} onClick={props.onAdd}>
            {t('BTN_ADD', '추가')}
          </Button>
        )}
        {p.U && props.onEdit && (
          <Button icon={<EditOutlined />} onClick={props.onEdit}>
            {t('BTN_EDIT', '수정')}
          </Button>
        )}
        {p.D && props.onDelete && (
          <Button icon={<DeleteOutlined />} onClick={props.onDelete} danger>
            {t('BTN_DELETE', '삭제')}
          </Button>
        )}
        {(p.C || p.U || p.D) && props.onSave && (
          <Button
            icon={<SaveOutlined />}
            onClick={props.onSave}
            type="primary"
            disabled={!props.dirty}
          >
            {t('BTN_SAVE', '저장')}
          </Button>
        )}
        {props.onCancel && (
          <Button icon={<CloseOutlined />} onClick={props.onCancel}>
            {t('BTN_CANCEL', '취소')}
          </Button>
        )}
        {p.E && props.onExport && (
          <Button icon={<DownloadOutlined />} onClick={props.onExport}>
            {t('BTN_EXCEL', '엑셀')}
          </Button>
        )}
        {p.P && props.onPrint && (
          <Button icon={<PrinterOutlined />} onClick={props.onPrint}>
            {t('BTN_PRINT', '출력')}
          </Button>
        )}
      </Space>
    </Flex>
  )
}
